import { useEffect, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useReducedMotion, useScroll } from "framer-motion";
import { SiWhatsapp } from "react-icons/si";
import { X } from "lucide-react";
import { track } from "@/lib/analytics";
import { WHATSAPP_DEFAULT } from "@/lib/offer";

const NUDGE_KEY = "ambitie:whatsapp-nudge";
const NUDGE_DELAY_MS = 15000;
const NUDGE_VISIBLE_MS = 8000;

function nudgeSeen() {
  try {
    return window.sessionStorage.getItem(NUDGE_KEY) === "1";
  } catch {
    return false;
  }
}

function markNudgeSeen() {
  try {
    window.sessionStorage.setItem(NUDGE_KEY, "1");
  } catch {
    // Storage blocked: the nudge may repeat, which is harmless.
  }
}

// Hidden while the hero CTA is on screen. Once visible, it nudges a single
// time per session: one pulse and a short "same-day reply" bubble.
export default function WhatsAppButton() {
  const { scrollY } = useScroll();
  const reduce = useReducedMotion();
  const [visible, setVisible] = useState(false);
  const [nudge, setNudge] = useState(false);

  useMotionValueEvent(scrollY, "change", (y) => {
    setVisible(y > window.innerHeight * 0.6);
  });

  useEffect(() => {
    if (!visible || nudgeSeen()) return undefined;
    const show = setTimeout(() => {
      markNudgeSeen();
      setNudge(true);
    }, NUDGE_DELAY_MS);
    return () => clearTimeout(show);
  }, [visible]);

  useEffect(() => {
    if (!nudge) return undefined;
    const hide = setTimeout(() => setNudge(false), NUDGE_VISIBLE_MS);
    return () => clearTimeout(hide);
  }, [nudge]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.6, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.6, y: 20 }}
          transition={{ type: "spring", stiffness: 380, damping: 26 }}
          className="fixed bottom-20 right-5 z-40 flex items-center gap-3 md:bottom-6"
        >
          <AnimatePresence>
            {nudge && (
              <motion.div
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }}
                transition={{ duration: 0.3 }}
                role="status"
                className="flex max-w-[15rem] items-center gap-2 rounded-xl border border-border bg-background py-2.5 pl-4 pr-2 text-sm text-foreground shadow-[0_12px_28px_-12px_rgba(17,26,51,0.35)]"
              >
                <span>
                  <span className="font-semibold">Resposta no mesmo dia.</span> Fale direto com o Bruno.
                </span>
                <button
                  type="button"
                  onClick={() => setNudge(false)}
                  aria-label="Fechar aviso"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
                >
                  <X className="h-4 w-4" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <a
            href={WHATSAPP_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("cta_click", { location: "whatsapp-float", nudged: nudge })}
            data-testid="whatsapp-float-button"
            aria-label="Falar no WhatsApp"
            className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_12px_28px_-10px_rgba(17,26,51,0.45)] transition-transform duration-200 hover:scale-105 active:scale-95"
          >
            {nudge && !reduce && (
              <motion.span
                aria-hidden="true"
                initial={{ opacity: 0.6, scale: 1 }}
                animate={{ opacity: 0, scale: 1.8 }}
                transition={{ duration: 1.4, repeat: 2, ease: "easeOut" }}
                className="absolute inset-0 rounded-full bg-[#25D366]"
              />
            )}
            <SiWhatsapp aria-hidden="true" className="relative h-7 w-7" />
          </a>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
